import React, { useContext, useState } from 'react';
import { CalendarOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, message } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ShopType } from '../../../../types/shop';
import { JUST_IN_CLUBHOUSE } from '../../../../constants/define/translation';
import { deleteTileWidgetRepository } from '../../../../repository/mytm/shop/shopRepository';
import { btnActive } from '../../../../styles';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { CONFIG_CONTAINER_NAME, deleteBlob } from '../../../../utils/azure';

interface ActionsColumnProps {
  record: ShopType;
  setIsOpenModal: Function;
  setDataRecord: Function;
  onRefresh: () => void;
  total: number;
  handleChangePageActive: Function;
  setIsOpenScheduleModal: Function;
  handleGetScheduleWidgetByID: Function;
  setLoading?: (value: boolean) => void;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(JUST_IN_CLUBHOUSE);
  const {
    record,
    setIsOpenModal,
    setDataRecord,
    onRefresh,
    handleChangePageActive,
    total,
    setIsOpenScheduleModal,
    handleGetScheduleWidgetByID,
    setLoading,
  } = props;
  const userContext = useContext(UserContext);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.delete')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setDeleting(true);
        const updateFunc = () => {
          deleteTileWidgetRepository(record?.id)
            .then(res => {
              deleteBlob(record?.imageLink, CONFIG_CONTAINER_NAME.UPLOADS).then().catch();
              message.success(t('message.success.deleteJustInClubhouse'));
            })
            .catch(() => message.error(MESSAGE_ERROR.M02))
            .finally(() => {
              const numRecord = total - 1;
              if (numRecord && numRecord > 0) {
                onRefresh();
              } else {
                handleChangePageActive(1);
              }
              setDeleting(false);
            });
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setDeleting(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleEdit = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.edit')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading && setLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              setDataRecord(record);
              setIsOpenModal(true);
            })
            .catch((e: any) => {
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setLoading && setLoading(false);
            });
        }
      },
      onCancel() {
        // NOT
      },
    });
  };
  const handleSchedule = async () => {
    await handleGetScheduleWidgetByID(record.id);
    setIsOpenScheduleModal(true);
  };

  return (
    <StyledContainer>
      <Space size="small">
        <Tooltip title={t('table.tooltip.edit')}>
          <Button icon={<EditOutlined />} onClick={handleEdit} />
        </Tooltip>
        <Tooltip title={t('table.tooltip.schedule')}>
          <Button icon={<CalendarOutlined />} onClick={handleSchedule} />
        </Tooltip>
        <Tooltip title={t('table.tooltip.delete')}>
          <Button
            icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
            onClick={handleDelete}
            disabled={deleting}
            loading={deleting}
          />
        </Tooltip>
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.5rem;
  ${btnActive}
`;
